import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { AuthService } from '../../services/auth.service';
import { CustomerService } from '../../services/customer.service';
import type { CustomerDTO, MitTransactionDTO, MitTransactionStatusValue } from '../../core/models';
import {
  MIT_TRANSACTION_STATUS_LABELS,
  MIT_TRANSACTION_TYPE_LABELS,
  MitService,
  mitRejectionLabel,
  mitTransactionStatusSeverity
} from '../../services/mit.service';
import { MitTransactionReadComponent } from './mit-transaction-read.component';

type LabeledCustomer = CustomerDTO & { displayLabel: string };

@Component({
  selector: 'app-mit-transactions-list',
  templateUrl: './mit-transactions-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .mit-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        position: sticky;
        top: 0;
        z-index: 20;
      }
      :host ::ng-deep .mit-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep p-table,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .mit-transaction-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .mit-transaction-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    AutoCompleteModule,
    DatePickerModule,
    DrawerModule,
    MatIconModule,
    MitTransactionReadComponent
  ]
})
export class MitTransactionsListComponent {
  private mitService = inject(MitService);
  private customerService = inject(CustomerService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private customerQuery = new Subject<string>();

  readonly canExportTransactions = this.auth.hasRole('MIT_TRANSACTION_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportTransactions) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay transacciones para exportar con los filtros actuales';
    }
    return 'Descargar todas las transacciones filtradas en CSV';
  }

  transactions = signal<MitTransactionDTO[]>([]);
  loading = signal(true);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  totalItems = signal(0);
  currentPage = signal(0);
  pageSize = signal(20);
  sortField = signal('createdAt');
  sortOrder = signal(-1);
  hasActiveFilters = signal(false);
  customerOptions = signal<LabeledCustomer[]>([]);

  /** Drawer de detalle (detalle vía GET /mit-transactions/{id}). */
  isDetailsOpen = signal(false);
  selectedTransactionId = signal<number | null>(null);

  readonly pageSizeOptions = [10, 20, 50];
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly statusOptions = Object.entries(MIT_TRANSACTION_STATUS_LABELS).map(([value, label]) => ({ value, label }));
  readonly typeOptions = Object.entries(MIT_TRANSACTION_TYPE_LABELS).map(([value, label]) => ({ value, label }));

  filtersForm = this.fb.group({
    status: '',
    type: '',
    customer: this.fb.control<LabeledCustomer | string | null>(null),
    mandateNumber: '',
    externalReference: '',
    createdAtRange: this.fb.control<Date[] | null>(null)
  });

  constructor() {
    this.customerQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe((query) => {
      this.customerService.listCustomers({
        'legalName[lk]': query,
        'comercialName[lk]': query,
        'taxId[lk]': query
      }, { page: 0, size: 20 }).subscribe((response) => {
        this.customerOptions.set((response.body ?? []).map((customer) => ({
          ...customer,
          displayLabel: customer.comercialName || customer.legalName || customer.taxId || ''
        })));
      });
    });
    for (const control of [this.filtersForm.controls.mandateNumber, this.filtersForm.controls.externalReference]) {
      control.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed())
        .subscribe(() => this.applyFilters());
    }
    this.filtersForm.controls.status.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.filtersForm.controls.type.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.filtersForm.controls.createdAtRange.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.load();
  }

  onCustomerSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.customerOptions.set([]);
      return;
    }
    this.customerQuery.next(query);
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    this.mitService.searchTransactions(this.buildSpec(), {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    }).subscribe({
      next: (response) => {
        this.transactions.set(response.body ?? []);
        this.totalItems.set(response.page?.totalItems ?? response.body?.length ?? 0);
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: () => {
        this.error.set('No se pudieron cargar las transacciones.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged = nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged ? 0 : event.rows ? Math.floor((event.first ?? 0) / event.rows) : 0;
    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) {
      return;
    }
    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.load();
  }

  applyFilters(): void {
    this.hasActiveFilters.set(Object.keys(this.buildSpec()).length > 0);
    this.currentPage.set(0);
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      status: '', type: '', customer: null, mandateNumber: '', externalReference: '', createdAtRange: null
    }, { emitEvent: false });
    this.customerOptions.set([]);
    this.applyFilters();
  }

  exportCsv(): void {
    if (!this.canExportTransactions || this.exporting()) return;
    this.exporting.set(true);
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    this.mitService.exportTransactionsCsv(this.buildSpec(), [`${this.sortField()},${direction}`]).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de transacciones descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de transacciones';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
      }
    });
  }

  open(tx: MitTransactionDTO): void {
    if (!tx.id) return;
    this.selectedTransactionId.set(tx.id);
    this.isDetailsOpen.set(true);
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedTransactionId.set(null);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  goToDetail(): void {
    const id = this.selectedTransactionId();
    if (!id) return;
    this.closeDetails();
    void this.router.navigate(['/mit-transactions', id]);
  }

  onTransactionChanged(tx: MitTransactionDTO): void {
    this.transactions.update((rows) => rows.map((row) => (row.id === tx.id ? { ...row, ...tx } : row)));
  }

  statusLabel(status: MitTransactionStatusValue | undefined): string {
    return status ? MIT_TRANSACTION_STATUS_LABELS[status] : '—';
  }

  typeLabel(type: MitTransactionDTO['type']): string {
    return type ? MIT_TRANSACTION_TYPE_LABELS[type] : '—';
  }

  detail(tx: MitTransactionDTO): string {
    if (tx.status === 'REJECTED') return mitRejectionLabel(tx.rejectionCode);
    if (tx.status === 'DECLINED') return tx.providerMessage ?? '';
    return '';
  }

  statusSeverity = mitTransactionStatusSeverity;

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'transacciones-bajo-demanda.csv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private buildSpec(): Record<string, string> {
    const raw = this.filtersForm.getRawValue();
    const spec: Record<string, string> = {};
    if (raw.status) spec['status'] = raw.status;
    if (raw.type) spec['type'] = raw.type;
    const customer = raw.customer;
    if (customer && typeof customer !== 'string' && customer.id != null) spec['customerId'] = String(customer.id);
    const mandateNumber = raw.mandateNumber?.trim();
    if (mandateNumber) spec['mandateNumber'] = mandateNumber;
    const reference = raw.externalReference?.trim();
    if (reference) spec['externalReference[lk]'] = reference;
    const range = raw.createdAtRange;
    if (range?.[0] && range?.[1]) {
      const from = new Date(range[0]);
      from.setHours(0, 0, 0, 0);
      const toExclusive = new Date(range[1]);
      toExclusive.setHours(0, 0, 0, 0);
      toExclusive.setDate(toExclusive.getDate() + 1);
      spec['createdAt[gte]'] = from.toISOString();
      spec['createdAt[lt]'] = toExclusive.toISOString();
    }
    return spec;
  }
}
